namespace Core.Booking {

    export enum BookingStatus {
        Complete = 1,
        Alert = 2,
        ThreeDs = 3,
        Error = 4,
        ValidationError = 5,
        TimedOut = 6,
        BookingError = 7,
        PayPalVerification = 8,
        PayPalVerified = 9,
        Cpg = 10,
        Refresh = 13,
        HostedPayment = 16
    }

    export interface TrimmedBooking {
        BookingProcessId: string;
        UserData: UserData;
        LinkedBookingId: number | undefined;
    }

    export interface Booking extends TrimmedBooking {
        PaymentOptions: PaymentCardType[];
        SavedPaymentCards: SavedPaymentCard[];
        InsuranceBands: InsuranceBand[];
        BookingRules: BookingRules;
        PaymentRules: PaymentRules;
        TermsData: TermsData;
        AcceptanceText: string;
        AgentScripts: KeyValuePairOfStringAndString[];
        DeliveryOptions: DeliveryOption[];
    }

    export enum PassengerType {
        None = 0,
        Adult = 1,
        Child = 2,
        Infant = 3,
        AdultSwimmer = 4,
        AdultNonSwimmer = 5,
        ChildSwimmer = 6,
        ChildNonSwimmer = 7,
    }

    export interface BookingRules {
        ForcePayment: boolean;
        IsStartDateMandatory: boolean;
        IsEveryPassengerRequired: boolean;
        IsEmailMandatory: boolean;
        IsPostCodeMandatory: boolean;
        EnablePostCodeLookup: boolean;
        AddressRequired: boolean;
        CaptureFlightDetails: boolean;
        CaptureHotelDetails: boolean;
        CaptureExcursionDate: boolean;
        IsDOBRequiredForAllAdults: boolean;
        IsDOBRequiredForAllChildren: boolean;
        IsDOBRequiredForAllInfants: boolean;
        IsFlightPlus: boolean;
        NationalityRequired: boolean;
        CheckCardExpiryAgainstArrivalDate: boolean;
        CaptureSMSAlerts: boolean;
        ShowCardFeeIfZero: boolean;
        LockEmailControls: boolean;
        PassengerTitle: string;
        NumberOfDrivers: number;
        PassengerPrompts: string[];
        CanChangeBusinessCustomer: boolean;
        ShowBusinessCustomerList: boolean;
        WillAutoDebitBalance: boolean;
        CaptureOfficeLocationInAgentMode: boolean;
        CaptureMarhabaConnectingFlight: boolean;
        CaptureChauffeurDetails: boolean;
        AllowChauffeurTimeEdit: boolean;
        ChauffeurMinAdvanceHours: number;
        ChauffeurTimeWindow: number;
        PassengerFirstNameMinLength: number;
        CaptureHolidayStartDate: boolean;
        ProductRules: ProductRules | null | undefined;
        MultiProductBehaviour: boolean;
    }

    export interface PaymentRules {
        BalanceAmount: number;
        BalanceDueDate: string;
        BalanceDueWeeksText: string | null;
        IsDepositAllowed: boolean;
        IsFullAmountAllowed: boolean;
        MinimumDepositAmount: number;
        TotalAmount: number;
        TravelAgentCommissionAmount: number;
        TravelAgentVat: number;
    }

    export type ProductRules =
        AccommodationProductRules |
        FlightProductRules |
        TransferProductRules;

    export interface AccommodationProductRules {
        ProductType: number;
        ProductGuid: string;
        RoomRules: RoomRules[];
    }

    export interface RoomRules {
        LinkedBkItemSeqNo: number;
        PassengerRules: PassengerRules;
        RoomNumber: number;
    }

    export interface FlightProductRules {
        ProductType: number;
        ProductGuid: string;
        PassengerRules: PassengerRules;
    }

    export interface TransferProductRules {
        ProductType: number;
        ProductGuid: string;
        ProposedInboundFlightProductGuid: string;
        ProposedOutboundFlightProductGuid: string;
        PassengerRules: PassengerRules;
        OutboundLeg: TransferLegRules;
        InboundLeg: TransferLegRules;
    }

    export interface TransferLegRules {
        CaptureDeparturePointDescription: boolean;
        CaptureDepartureFlightArrivalTime: boolean;
        CaptureDepartureFlightNumber: boolean;
        CaptureDepartureFlightOrigin: boolean;
        CaptureDestinationPointDescription: boolean;
        CaptureDestinationFlightDepartureTime: boolean;
        CaptureDestinationFlightNumber: boolean;
        CaptureDestinationFlightDestination: boolean;
    };

    export interface PassengerRules {
        AgeAtDate: string;
        NumAdults: number;
        NumChildren: number;
        NumInfants: number;
        ChildrenAges: number[];
        InfantAges: number[];
        AllAdultsDOBRequired: boolean;
        AllChildrenDOBRequired: boolean;
        AllInfantDOBRequired: boolean;
    }

    export interface TermsData {
        Suppliers: number[];
        TransferRateId: number;
    }

    export interface UserData {
        Contact: Contact;
        Rooms: Room[];
        PaymentDetails: PaymentDetails;
        ConnectingFlight: ConnectingFlight;
        HotelDetails: HotelDetails;
        ChauffeurDetails: ChauffeurDetails;
        AirportParking: AirportParking;
        AttractionDetails: AttractionDetails[];
        Companions: Companion[];
        RequirementsList: Requirement[];
        HolidayStartDate: string;
        DeliverySupplierId: number;
        ProductDetails: ProductDetails[];
        MultiProductBehaviour: boolean;
        TravelAgent: Agent;
        Notes: Note[];
        InfoItems: InfoItem[] | null | undefined;
        CovidDisclaimer: boolean;
    }

    export type ProductDetails =
        ProductPassengerDetails |
        ProductHotelDetails |
        ProductTransferDetails |
        ProductMarhabaDetails;

    export interface ProductPassengerDetails {
        ProductGuid: string;
        ProductType: number;
        PassengerList: Core.Booking.Person[];
    }

    export interface ProductHotelDetails extends ProductPassengerDetails {
        RequirementsList: Requirement[];
    }

    export interface ProductTransferDetails extends ProductPassengerDetails {
        OutboundLeg: ProductDetailsTransferLeg;
        InboundLeg: ProductDetailsTransferLeg;
    }

    export interface ProductMarhabaDetails extends ProductPassengerDetails {
        ConnectingFlight: {
            OutboundFlightNumber: string;
            OutboundFlightClass: string;
        };
    }

    export interface ProductDetailsTransferLeg {
        DepartureFlightNumber: string;
        DepartureFlightArrivalTime: string;
        DestinationFlightNumber: string;
        DestinationFlightDepartureTime: string;
        DeparturePointDescription?: string;
        DepartureFlightOrigin?: string;
        DestinationPointDescription?: string;
        DestinationFlightDestination?: string;
    }

    export interface Person {
        Title: string;
        FirstName: string;
        LastName: string;
        Age: number;
        DateOfBirth: string | undefined;
        Type: PassengerType;
        DriverType: DriverType;
        InsuranceBandId: number;
        LinkedBkItemSeqNo: number;
        LoyaltyNumber: string;
        Id: number;
        PassengerFirstNameMinLength: number;
        Label: string;
        RequiresDob: boolean;
    }

    export interface Contact extends Person {
        CountryCode: string;
        PostCode: string;
        Address: string;
        City: string;
        County: string;
        HomeTel: string;
        MobileTel: string;
        WorkTel: string;
        Email: string;
        SendTextAlerts: boolean;
        EmailSignUp: boolean;
        EmailOptOut: boolean;
        AgreeToConditions: boolean;
        BookingCallBack: boolean;
    }

    export interface PaymentDetails {
        PaypalDetails: PaypalDetails;
        CardDetails: CardDetails;
        SaveThisCard: boolean;
        CanDebitBalance: boolean;
        DepositAmount: number;
        IsSkippingPayment: boolean;
        PrePaymentID: string;
        PaRes: string;
        MD: string;
        OfficeId: number;
        CustomerId: number;
        CustomerName: string;
        ExternalFopDetails: ExternalFopDetails;
        IsExternalFop: boolean;
        PaymentAmount: number;
        DfReferenceId: string;
    }

    export interface ConnectingFlight {
        OutboundFlightNumber: string;
        OutboundDate: string;
        OutboundDepartureAirport: string;
        OutboundDepartureAirportName: string;
        OutboundDestinationAirport: string;
        OutboundDestinationAirportName: string;
        InboundFlightNumber: string;
        InboundDate: string;
        InboundDepartureAirport: string;
        InboundDepartureAirportName: string;
        InboundDestinationAirport: string;
        InboundDestinationAirportName: string;
        InboundFlightClass: string;
        OutboundFlightClass: string;
    }

    export interface HotelDetails {
        Name: string;
        Address: string;
    }

    export interface ChauffeurDetails {
        Address: string;
        Building: string;
        ContactNo: string;
        Landmark: string;
        PickUpTime: string;
    }

    export interface AirportParking {
        CarMake: string;
        CarModel: string;
        CarColour: string;
        CarRegistration: string;
    }

    export interface Agent {
        FirstName: string;
        LastName: string;
        Title: string;
    }

    export enum DriverType {
        NotADriver = 0,
        MainDriver = 1,
        AdditionalDriver = 2,
    }

    export interface PaypalDetails {
    }

    export interface CardDetails {
        PaymentTypeId: number | null;
        CardType: number | null;
        CardNumber: string | null;
        MaskedCardNumber: string | null;
        StartDate: any | null;
        ExpiryDate: any | null;
        SecurityCode: string | null;
        IssueNumber: string | null;
        CardId: string | null;
    }

    export interface ExternalFopDetails {
        CardType: number | null;
        AuthorisationCode: string | null;
        Note: string | null;
    }

    export interface PaymentCardType {
        CardTypeId: number;
        PaymentTypeId: number;
        FeePercent: number;
        FeeAmount: number;
        FeeCap: number;
        Title: string;
        DataCashScheme: string;
        AdminOverride: boolean;
        ImageUrl: string;
        IsBankPayment: boolean;
        Type: string;
        IsPayPalPayment: boolean;
        FullDetails: boolean;
        PreAuthorisationToken: string;
        IsExternalFop: boolean;
    }

    export interface SavedPaymentCard {
        CardId: string;
        CardTypeId: number;
        ExpiryYear: number;
        MasterCardTypeId: number;
        ExpiryMonth: number;
        IsExpired: boolean;
        LogoName: string;
        CardName: string;
        MaskedNumber: string;
    }

    export interface InsuranceBand {
        Id: number;
        StartAge: number;
        EndAge: number;
        PassengerType: PassengerType;
        Price: number;
        Available: boolean;
        Label: string;
    }

    export interface ValueType {
    }

    export interface KeyValuePairOfStringAndString extends ValueType {
        Key: string;
        Value: string;
    }

    export interface DeliveryOption {
        SupplierId: number;
        Price: number;
        CurrencyCode: string;
        Title: string;
        Selected: boolean;
    }

    export interface Room {
        Guests: Person[];
        SpecialRequest: string;
        LinkedBkItemSeqNo: number;
    }

    export interface AttractionDetails {
        ProductId: number;
        ProductTitle: string;
        ProductDescription: string;
        PreferredDate: string;
        RestrictionText: string;
        CaptureDate: boolean;
    }

    export interface Companion {
        Title: string;
        FirstName: string;
        LastName: string;
        DateOfBirth: string | undefined;
        EstimatedAge: number;
        TravelDate: string;
        LoyaltyNumber: string;
    }

    export interface Requirement {
        DisplayRoomNumber: number;
        RequirementsText: string;
        RoomId: number;
    }

    export interface Note {
        Id: string;
        Text: string;
        Created: string;
        CreatedById: number;
        CreatedBy: string;
    }

    export interface InfoItem {
        Title: string;
        Description: string;
        Important: boolean;
        Voucher: boolean;
    }

    export interface BookingResponse {
        Status: CreateBookingStatus;
        Messages: string[];
        BookingReferences: string[];
        ThreeDSecure: ThreedsecureResponse;
        PayPal: PayPalResponse;
        RefreshPrepareBookingResponse: Booking;
        RefreshResponse: RefreshResponse;
    }

    export interface RefreshResponse
    {
        Basket: object;
        StaleProducts: ProductAndNewId[];
    }

    export interface ProductAndNewId
    {
        Product: RefreshProduct;
        NewProductId: string;
    }

    export interface RefreshProduct
    {
        ProductId: string;
        ProductType: number;
        TotalAmount: number;
    }

    export enum CreateBookingStatus {
        Complete = 1,
        Alert = 2,
        ThreeDs = 3,
        Error = 4,
        ValidationError = 5,
        TimedOut = 6,
        BookingError = 7,
        PayPalVerification = 8,
        PayPalVerified = 9,
        Cpg = 10,
        Refresh = 13,
        HostedPayment = 16
    }

    export interface ThreedsecureResponse {
        AcsUrl: string;
        TermUrl: string;
        PaReq: string;
        TL: string;
        MD: string;
        CanByPass: boolean;
        PrePaymentID: string;
    }

    export interface PayPalResponse {
        AcsUrl: string;
    }

    export enum ProductType {
        Flight = 1,
        Attraction = 2,
        CarHire = 4,
        Hotel = 8,
        Parking = 16,
        Transfer = 32,
        Insurance = 64,
        Marhaba = 256,
        Cruise = 512
    }

    export enum RecentSearchType {
        Flights = 1,
        Hotels = 2,
        Holidays = 3
    }

    // This enum is only used client side in order to provide a common type for
    // standard products cross-sells (e.g. transfers/attractions) and
    // flight extras cross sells (e.g. bags/seats)
    export enum CrossSellType {
        Hotels = 1,
        Attractions = 2,
        Transfers = 3,
        CarHire = 4,
        Seats = 5,
        Baggage = 6,
        Parking = 7
    }

}
